/**
 * Direct links to Help Center articles, for pasting into an email.
 *
 * A link opens the Help panel on that article, full width, in whichever app it
 * points at:
 *   ?help_article=<Confluence page id>   articles imported from Confluence. Survives a re-import.
 *   ?help_article_id=<article id>        articles written in the knowledge base (no Confluence id).
 *
 * Articles shown only to stores open in The Sweet Spot, articles stores cannot read open in Cockpit,
 * and articles shown to both stay in the app the link is copied from.
 * The host comes from the page the link is copied on, so a link copied on dev or stage
 * points at dev or stage. The same module is mirrored in The Sweet Spot.
 */

export type LinkApp = 'cockpit' | 'sweetspot';

export type ArticleLinkSource = {
  id: number;
  legacy_confluence_id?: string | null;
  audience?: readonly string[] | null;
};

/** Store-only articles belong to The Sweet Spot. No audience information means the current app is right. */
export function isStoreOnlyAudience(audience: readonly string[] | null | undefined): boolean {
  return !!audience && audience.length > 0 && audience.every((a) => a === 'stores');
}

/** Which app a link to an article should open in. `current` is the app the link is copied from. */
export function targetAppForAudience(audience: readonly string[] | null | undefined, current: LinkApp): LinkApp {
  if (!audience || audience.length === 0) return current;
  const hasStores = audience.includes('stores');
  if (!hasStores) return 'cockpit';
  return isStoreOnlyAudience(audience) ? 'sweetspot' : current;
}

/** The query string (with its leading `?`) that opens this article. */
export function articleLinkQuery(a: ArticleLinkSource): string {
  const legacy = a.legacy_confluence_id?.trim();
  if (legacy && /^\d{1,32}$/.test(legacy)) return `?help_article=${legacy}`;
  return `?help_article_id=${a.id}`;
}

/** Swap `cockpit` for `sweetspot` (or back) in a hostname like `dev.cockpit.ampersandbrands.com`. Other hosts come back unchanged. */
export function hostnameForApp(hostname: string, app: LinkApp): string {
  const from = app === 'sweetspot' ? 'cockpit' : 'sweetspot';
  return hostname.replace(new RegExp(`(^|\\.)${from}(\\.)`), `$1${app}$2`);
}

/**
 * The full URL to copy for an article. `currentApp` is the app doing the copying and `origin`
 * is that page's origin (`window.location.origin`).
 */
export function buildArticleLink(a: ArticleLinkSource, currentApp: LinkApp, origin: string): string | null {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return null;
  }
  const target = targetAppForAudience(a.audience, currentApp);
  if (target !== currentApp) url.hostname = hostnameForApp(url.hostname, target);
  return `${url.origin}/${articleLinkQuery(a)}`;
}

/** Builds links for the page the person is on. Pass to the Help panel's `buildArticleLink`. */
export function buildCurrentArticleLink(a: ArticleLinkSource): string | null {
  if (typeof window === 'undefined') return null;
  return buildArticleLink(a, 'cockpit', window.location.origin);
}

/** The id in a `?help_article_id=` value, or null when it is not a positive whole number. */
export function parseHelpArticleId(raw: string | null): number | null {
  if (raw === null || !/^\d{1,9}$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return n > 0 ? n : null;
}
